import {
  PublicClientApplication,
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { msalConfig, loginRequest, tokenRequest } from './authConfig';

class AuthService {
  private msalInstance: PublicClientApplication;
  private account: AccountInfo | null = null;

  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
  }

  async initialize(): Promise<void> {
    await this.msalInstance.initialize();
    await this.msalInstance.handleRedirectPromise();

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      this.account = accounts[0];
    }
  }

  async login(): Promise<AccountInfo> {
    try {
      const response: AuthenticationResult = await this.msalInstance.loginPopup(loginRequest);
      this.account = response.account;
      return response.account;
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error('Authentication failed. Please try again.');
    }
  }

  async getAccessToken(): Promise<string> {
    if (!this.account) {
      throw new Error('No account found. Please login first.');
    }

    try {
      // Try to acquire token silently first
      const response = await this.msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: this.account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // If silent acquisition fails, use popup
        try {
          const response = await this.msalInstance.acquireTokenPopup(tokenRequest);
          return response.accessToken;
        } catch (popupError) {
          console.error('Token acquisition failed:', popupError);
          throw new Error('Failed to acquire access token. Please login again.');
        }
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    if (this.account) {
      await this.msalInstance.logoutPopup({ account: this.account });
      this.account = null;
    }
  }

  isAuthenticated(): boolean {
    return this.account !== null;
  }

  getAccount(): AccountInfo | null {
    return this.account;
  }
}

export const authService = new AuthService();
